import type { AttributeEditModeConfig } from '@source-components-react/types';
import {
  ACCORDION_DETAIL_ATTRIBUTE,
  ACCORDION_HEADING_LEVEL_ATTRIBUTE,
  ACCORDION_INLINE_STYLES_ATTRIBUTE,
  ACCORDION_LINKS_ATTRIBUTE,
  ACCORDION_OPEN_ATTRIBUTE,
  ACCORDION_SUMMARY_ATTRIBUTE,
  DEFAULT_ACCORDION_HEADING_LEVEL,
} from './accordion-attributes';

/**
 * Every setting the accordion offers, in the order the Modify drawer shows
 * them.
 *
 * Headings name the two halves of the element — Summary and Detail — not one
 * use of it. An author building an FAQ reads "Summary" as the question without
 * being told to; an author building a "show more" would have had to translate
 * "Question" into something it is not.
 *
 * Order is not cosmetic. The drawer renders these top to bottom and the
 * documentation registry asserts its pages match that sequence, so moving an
 * entry here moves it in both places at once.
 */
export const attributeEditModeConfigs: AttributeEditModeConfig[] = [
  {
    name: ACCORDION_SUMMARY_ATTRIBUTE,
    heading: 'Summary',
    subHeading:
      'The line a reader sees closed and clicks to expand. One line — extra lines collapse into it, for 3 different device resolutions',
    defaultValues: {},
  },
  {
    name: ACCORDION_DETAIL_ATTRIBUTE,
    heading: 'Detail',
    subHeading:
      'What the summary expands to, one paragraph per line. Blank lines are skipped, for 3 different device resolutions',
    defaultValues: {},
  },
  {
    name: ACCORDION_OPEN_ATTRIBUTE,
    heading: 'Open by Default',
    subHeading:
      'Whether the detail is already expanded when the page loads. A reader can still collapse it, for 3 different device resolutions',
    defaultValues: {},
  },
  {
    name: ACCORDION_HEADING_LEVEL_ATTRIBUTE,
    heading: 'Summary Heading Level',
    subHeading:
      'Whether the summary also counts as a heading in the page outline, and at which level. Leave it at none unless these summaries are the structure of the page, for 3 different device resolutions',
    defaultValues: { all: DEFAULT_ACCORDION_HEADING_LEVEL },
  },
  {
    name: ACCORDION_LINKS_ATTRIBUTE,
    heading: 'Links',
    subHeading:
      'Links the detail can point at. To link part of the detail, wrap those words in {1|linked text}, where the number is the position of the entry in this list counting from 1; clearing an entry leaves its position in place so the other numbers keep pointing where they did, for 3 different device resolutions',
    defaultValues: {},
  },
  {
    name: ACCORDION_INLINE_STYLES_ATTRIBUTE,
    heading: 'Class Names & Styles',
    subHeading:
      'Class names and CSS the detail can apply to part of itself, e.g. to give a few words their own colour. To apply an entry, wrap those words in {.1|styled text}, where the number is the position of the entry in this list counting from 1; clearing an entry leaves its position in place so the other numbers keep pointing where they did, for 3 different device resolutions',
    defaultValues: {},
  },
];
